import cn from "classnames";
import { Map as LeafletMap } from "components/map/shared";
import BookShow from "components/shared/book/show";
import Collapse from "components/shared/collapse";
import AudioPlayer from "components/shared/fileBody/audio";
import Select from "components/shared/select";
import { dateTimeToView, formatUtcOffset } from "helpers/date";
import { avoidSpecialRoadFlags, avoidTollRoadFlags, fixRouteBidStatuses, fixRouteBidStatusesEnum } from "helpers/enums";
import { ending } from "helpers/i18n";
import { planLineStyle, taskMarkerStyle } from "helpers/map";
import { paths } from "helpers/paths";
import { viewContainer } from "helpers/redux";
import { noRolesOtherThan } from "helpers/roles";
import { allFlagValues, audioExtensions, enumLabel, imageExtensions } from "helpers/system";
import { divIcon } from "leaflet";
import React, { useEffect, useState } from "react";
import Lightbox from "react-18-image-lightbox";
import { Marker, Polyline, Tooltip } from "react-leaflet";
import { Link } from "react-router-dom";
import { getRoute } from "sources/map";
import Chat from "./chat";

const routeOptionsLabels = {
    routerProfile: {
        label: "Профиль",
        value: (value) => value ?? "Не выбрано",
    },
    disallowToll: {
        label: "Избегать все платные дороги",
        value: (value) => (value ? "Да" : value === false ? "Нет" : "Не выбрано"),
    },
    useSpecialProfileForEmptyRoute: {
        label: "Использовать отдельный профиль для порожних маршрутов",
        value: (value) => (value ? "Да" : value === false ? "Нет" : "Не выбрано"),
    },
    disallowTollExceptM4: {
        label: "Избегать все платные дороги, кроме М4",
        value: (value) => (value ? "Да" : value === false ? "Нет" : "Не выбрано"),
    },
    disallowTollExceptM12DRT: {
        label: "Избегать все платные дороги, кроме участка \"Дюртюли - Ачит\" М-12",
        value: (value) => (value ? "Да" : value === false ? "Нет" : "Не выбрано"),
    },
    avoidTollRoadFlags: {
        label: <b className="text-muted">Исключать платные дороги</b>,
        value: (value) => (value !== undefined ? allFlagValues(avoidTollRoadFlags, value) : "Не выбрано"),
    },
    avoidSpecialRoadFlags: {
        label: <b className="text-muted">Исключать особые дороги</b>,
        value: (value) => (value !== undefined ? allFlagValues(avoidSpecialRoadFlags, value) : "Не выбрано"),
    },
    allowFerry: {
        label: "Разрешить паромы",
        value: (value) => (value ? "Да" : value === false ? "Нет" : "Не выбрано"),
    },
    calcTolls: {
        label: "Рассчитывать платные дороги",
        value: (value) => (value ? "Да" : value === false ? "Нет" : "Не выбрано"),
    },
    country: {
        label: "Ограничение по стране",
        value: (value) => value ?? "Не выбрано",
    },
};

const getStatusBadgeClass = (status) => {
    switch (status) {
        case fixRouteBidStatusesEnum.Draft:
            return "badge-secondary";
        case fixRouteBidStatusesEnum.New:
            return "badge-info";
        case fixRouteBidStatusesEnum.InProgress:
            return "badge-primary";
        case fixRouteBidStatusesEnum.Completed:
            return "badge-success";
        case fixRouteBidStatusesEnum.Rejected:
            return "badge-danger";
        default:
            return "badge-light";
    }
};

const CurrentLocation = ({
    location,
    fixedAt,
    organizationTimezone,
    tileUrls,
    startGeozone,
    endGeozone,
    routeOptions,
}) => {
    const [route, setRoute] = useState([]);
    useEffect(() => {
        if (startGeozone && endGeozone) {
            const segmentCoords = [startGeozone.location.coordinates, endGeozone.location.coordinates];
            getRoute(segmentCoords, { routeOptions }).then((data) => setRoute(data?.concatedCoords || []));
        }
    }, []);
    const mapCenter = location
        ? [...location.coordinates].reverse()
        : startGeozone
          ? [...startGeozone.location.coordinates].reverse()
          : [...endGeozone.location.coordinates].reverse();
    const dateInTimezone = organizationTimezone?.id
        ? new Date(fixedAt).toLocaleString("ru-RU", { timeZone: organizationTimezone.id })
        : "";

    return (
        <div className="card mb-3">
            <div className="map card-body">
                {
                    <LeafletMap
                        className="map__current-position"
                        tileUrls={tileUrls}
                        maxZoom={18}
                        viewport={{ center: mapCenter, zoom: 5 }}
                    >
                        {location && (
                            <Marker
                                position={[...location.coordinates].reverse()}
                                icon={divIcon({
                                    iconSize: [28, 82],
                                    className: "map__icon map-icon map-icon--blue-track map-icon--car",
                                })}
                            >
                                <Tooltip permanent className="map__tooltip map__tooltip--vehicle" direction="top">
                                    <div>Обновлено в {dateInTimezone}</div>
                                </Tooltip>
                            </Marker>
                        )}
                        {route && <Polyline positions={route} {...planLineStyle} />}
                        {startGeozone && (
                            <Marker
                                position={[...startGeozone.location.coordinates].reverse()}
                                {...taskMarkerStyle}
                                icon={divIcon({ className: "route-icon", iconSize: [30, 30], html: "A" })}
                            />
                        )}
                        {endGeozone && (
                            <Marker
                                position={[...endGeozone.location.coordinates].reverse()}
                                {...taskMarkerStyle}
                                icon={divIcon({ className: "route-icon", iconSize: [30, 30], html: "B" })}
                            />
                        )}
                    </LeafletMap>
                }
            </div>
        </div>
    );
};

const renderDownloadLink = (url, name) => {
    return (
        <a href={url} download={name} className="link link--download" target="_blank" rel="noopener noreferrer">
            Скачать файл
        </a>
    );
};

const ImageBody = ({ name, url, accessUrlLink }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <>
            <div className="text-center">
                <img
                    src={accessUrlLink}
                    onClick={() => setIsOpen(true)}
                    className="chat-msg__body-image"
                    title={name}
                    alt={name}
                />
            </div>
            {isOpen && <Lightbox mainSrc={accessUrlLink} onCloseRequest={() => setIsOpen(false)} />}
            <div className="chat-msg__body-image--link">{renderDownloadLink(url, name)}</div>
        </>
    );
};

const AudioBody = ({ currentAudio, accessUrlLink }) => {
    return <AudioPlayer currentAudio={currentAudio} accessUrlLink={accessUrlLink} />;
};

class ShowFixRouteBid extends BookShow {
    get dropdActions() {
        return [];
    }

    changeStatus = ({ value }) => {
        if (!value) {
            return this.setState({ isEditingStatus: false });
        }
        const { id } = this.state.data;
        const config = {
            name: "confirm",
            title: "Смена статуса",
            text: `Вы уверены, что хотите изменить статус заявки на "${enumLabel(fixRouteBidStatuses, value)}"?`,
            action: () => {
                this.setState({ statusIsLoading: true });
                this.props.source
                    .setStatus({ id, status: value })
                    .then(() => {
                        this.setState({
                            statusIsLoading: false,
                            data: { ...this.state.data, status: value },
                            isEditingStatus: false,
                        });
                    })
                    .catch(() => this.setState({ statusIsLoading: false }));
            },
        };
        return this.props.modal(config);
    };

    renderFile = (file) => {
        const currentAudio = React.createRef();
        const name = file.originalFileName;
        const url = file.accessUrlLink.replace(name, "");
        const accessUrlLink = file.accessUrlLink;
        if (imageExtensions.includes(file.extension)) {
            return <ImageBody name={name} url={url} accessUrlLink={accessUrlLink} />;
        } else if (audioExtensions.includes(file.extension)) {
            return <AudioBody currentAudio={currentAudio} accessUrlLink={accessUrlLink} />;
        }
        return (
            <div className="align-items-center d-flex flex-column h-100">
                <div className="chat-msg__body-icon position-relative flex-grow-1" />
                <div className="chat-msg__body-text ellipsis text-center" title={name}>
                    {name}
                </div>
                {renderDownloadLink(url, name)}
            </div>
        );
    };

    renderRouteOptions = () => {
        const { routeOptions } = this.state.data;

        return Object.entries(routeOptions).map(([key, value]) => (
            <div key={key}>
                <span className="text-muted">{routeOptionsLabels[key]?.label ?? key}:</span>{" "}
                {routeOptionsLabels[key]?.value(value) ?? value}
            </div>
        ));
    };

    renderBody = () => {
        const {
            createdAt,
            organization,
            bidId,
            status,
            text,
            files,
            logs,
            location,
            startGeozone,
            endGeozone,
            routeOptions,
            routerType,
            chatId,
        } = this.state.data;
        const { roles, organizationTimezone } = this.props;
        const statusProps = {
            inputProps: { name: "status" },
            options: fixRouteBidStatuses,
            value: status,
            menuPortalTarget: document.body,
            onChange: this.changeStatus,
        };
        const canEdit = !(
            this.props.inOrgSection ||
            noRolesOtherThan(roles, ["isDiagnostics"]) ||
            status === fixRouteBidStatusesEnum.Draft
        );

        const timeInOrgTimezone = (date) => {
            const offset = formatUtcOffset(organizationTimezone);

            return organizationTimezone?.id
                ? `${new Date(date).toLocaleString("ru-RU", { timeZone: organizationTimezone.id })} (${offset})`
                : dateTimeToView(date, "DD.MM.YYYY HH:mm (Z)");
        };

        return (
            <div className="mb-3">
                <div className="font-size-14 card card-body">
                    <div className="mb-2">
                        <strong className="pr-2">Дата обращения:</strong>
                        {dateTimeToView(createdAt)}
                    </div>
                    {organization && (
                        <div className="mb-2">
                            <strong className="pr-2">Компания:</strong>
                            <Link to={paths.adminOrganizationsShow(organization.id)}>{organization.name}</Link>
                        </div>
                    )}
                    <div className="mb-2">
                        <strong className="pr-2">Заявка:</strong>
                        {this.props.inOrgSection ? (
                            <a href={paths.bidShow("bid", bidId)} rel="noopener noreferrer" target="_blank">
                                {bidId}
                            </a>
                        ) : (
                            bidId
                        )}
                    </div>
                    <div className="d-flex align-items-baseline mb-2">
                        <strong className="pr-2">Статус:</strong>
                        {this.state.isEditingStatus ? (
                            <div
                                className={cn("flex-grow-1 mw-200", {
                                    "loading loading--small": this.state.statusIsLoading,
                                })}
                            >
                                <Select {...statusProps} />
                            </div>
                        ) : (
                            <>
                                <span className={cn("badge", getStatusBadgeClass(status))}>
                                    {enumLabel(fixRouteBidStatuses, status)}
                                </span>
                                {canEdit && (
                                    <span
                                        className="link pl-2 small"
                                        onClick={() => this.setState({ isEditingStatus: true })}
                                    >
                                        <i className="icon-uEA10-pen inline-icon pr-1" />
                                        Изменить
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                    <div className="mb-2">
                        <div className="font-weight-bold">Описание:</div>
                        <div>{text}</div>
                    </div>
                    {!this.props.inOrgSection && routerType && (
                        <div className="mb-2">
                            <div className="font-weight-bold">Маршрутизатор:</div>
                            <div>{routerType}</div>
                        </div>
                    )}
                </div>
                {!this.props.inOrgSection && routeOptions && (
                    <Collapse title={<div className="font-weight-bold">Настройки маршрутизатора</div>} className="pt-4">
                        <div>{this.renderRouteOptions()}</div>
                    </Collapse>
                )}
                {files.length > 0 && (
                    <Collapse title={<div className="font-weight-bold">Прикрепленные файлы</div>} className="pt-4">
                        <div className="row">
                            {files.map((file, i) => (
                                <div
                                    key={i}
                                    className={
                                        audioExtensions.includes(file.extension) ? "col-12 pt-2" : "col-md-4 col-sm-6"
                                    }
                                >
                                    {this.renderFile(file)}
                                </div>
                            ))}
                        </div>
                    </Collapse>
                )}
                {logs && (
                    <Collapse
                        title={
                            <div>
                                <strong>История изменений</strong>
                                <small className="text-muted pl-2">
                                    ({logs.length} {ending(logs.length, "запись", "записи", "записей")})
                                </small>
                            </div>
                        }
                        className="pt-4"
                    >
                        <table className="table table-sm table-responsive-sm">
                            <thead>
                                <tr>
                                    <th>Дата</th>
                                    <th>Статус</th>
                                    <th>Пользователь</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((row, i) => (
                                    <tr key={i}>
                                        <td>{timeInOrgTimezone(row.fixedAt)}</td>
                                        <td>
                                            <span className={cn("badge", getStatusBadgeClass(row.status))}>
                                                {enumLabel(fixRouteBidStatuses, row.status)}
                                            </span>
                                        </td>
                                        <td>
                                            {row.userName}
                                            <small className="text-muted pl-2">(id={row.userId})</small>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Collapse>
                )}
                {(location || startGeozone || endGeozone) && (
                    <Collapse title={<div className="font-weight-bold">Карта</div>} className="pt-4">
                        <CurrentLocation
                            {...location}
                            organizationTimezone={this.props.organizationTimezone}
                            tileUrls={this.props.tileUrls}
                            startGeozone={startGeozone}
                            endGeozone={endGeozone}
                            routeOptions={routeOptions}
                            routerType={routerType}
                        />
                    </Collapse>
                )}
                {chatId && (
                    <Collapse title={<strong>Чат</strong>} className="pt-4">
                        <Chat chatId={String(chatId)} />
                    </Collapse>
                )}
            </div>
        );
    };
}

const ConfiguredShow = (props) => <ShowFixRouteBid {...props} />;

export { ConfiguredShow };
export default viewContainer(ConfiguredShow, "admin-fix-route-bids");
